import { AnswerFilePreview } from "@/components/license-details/cards/answer-file-preview";
import { PhotoAnswerLocationMap } from "@/components/license-details/cards/photo-answer-location-map";
import type { LatLng } from "@/lib/geo";
import type { PhotoAnswer } from "@/lib/photo-answer";
import {
  formatPhotoTimeRange,
  formatPropertyAddressCaption,
} from "@/lib/photo-map-pins";

export type PhotoAnswerReviewProperty = {
  latitude?: number | null;
  longitude?: number | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  display_address?: string | null;
};

type PhotoAnswerReviewProps = {
  photos: PhotoAnswer[];
  property?: PhotoAnswerReviewProperty | null;
  elevatedDialog?: boolean;
  className?: string;
};

/**
 * Operator/government photo answer review: embedded image + adjacent map,
 * with property address in the caption. Shared by inspection answers panel
 * and registration additional-questions card.
 */
export function PhotoAnswerReview({
  photos,
  property = null,
  elevatedDialog = false,
  className,
}: PhotoAnswerReviewProps) {
  const propertyLocation: LatLng | null =
    property != null &&
    typeof property.latitude === "number" &&
    typeof property.longitude === "number" &&
    Number.isFinite(property.latitude) &&
    Number.isFinite(property.longitude)
      ? { latitude: property.latitude, longitude: property.longitude }
      : null;

  const addressCaption = property
    ? formatPropertyAddressCaption(property)
    : null;

  return (
    <div
      className={className ?? "mt-1 space-y-3"}
      data-testid="photo-answer-review"
    >
      {photos.map((photo, index) => {
        const timeLabel = formatPhotoTimeRange([photo]);
        const integrityBits: string[] = [];
        if (
          typeof photo.accuracy_meters === "number" &&
          Number.isFinite(photo.accuracy_meters)
        ) {
          integrityBits.push(`±${Math.round(photo.accuracy_meters)} m GPS`);
        }
        if (
          typeof photo.distance_meters_from_property === "number" &&
          Number.isFinite(photo.distance_meters_from_property)
        ) {
          integrityBits.push(
            `${Math.round(photo.distance_meters_from_property)} m from property`,
          );
        }
        if (
          typeof photo.server_skew_ms === "number" &&
          Number.isFinite(photo.server_skew_ms)
        ) {
          const skewSec = Math.round(Math.abs(photo.server_skew_ms) / 1000);
          integrityBits.push(
            skewSec === 0 ? "time skew <1s" : `time skew ±${skewSec}s`,
          );
        } else if (
          typeof photo.clock_offset_ms === "number" &&
          Number.isFinite(photo.clock_offset_ms) &&
          Math.abs(photo.clock_offset_ms) >= 1000
        ) {
          integrityBits.push(
            `clock offset ${Math.round(photo.clock_offset_ms / 1000)}s`,
          );
        }
        return (
          <div
            key={`${photo.token}-${index}`}
            className="flex flex-col gap-2 sm:flex-row sm:items-start"
            data-testid="photo-answer-review-item"
          >
            <div className="min-w-0 flex-1 space-y-1">
              <AnswerFilePreview
                answer={photo}
                answerType="photo"
                elevatedDialog={elevatedDialog}
                caption={addressCaption}
                hideCaptureMetadata
              />
              {timeLabel ? (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="photo-answer-time"
                >
                  {timeLabel}
                </p>
              ) : null}
              {integrityBits.length > 0 ? (
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="photo-answer-integrity"
                >
                  {integrityBits.join(" · ")}
                </p>
              ) : null}
            </div>
            <PhotoAnswerLocationMap
              photo={photo}
              propertyLocation={propertyLocation}
            />
          </div>
        );
      })}
    </div>
  );
}
